import { useEffect, useState } from 'react';
import HourglassEmptyOutlinedIcon from '@mui/icons-material/HourglassEmptyOutlined';
import { Stack, Typography } from '@mui/material';
import { useDateService } from 'framework/utils';

const MINUTE_IN_MS = 60 * 1000;
const HOUR_IN_MS = 60 * MINUTE_IN_MS;
const DAY_IN_MS = 24 * HOUR_IN_MS;

export const getSlaDisplayValue = (differenceInMs: number, showLeftLabel = true) => {
  const absoluteDifference = Math.abs(differenceInMs);
  const [divider, unit] =
    absoluteDifference >= DAY_IN_MS
      ? [DAY_IN_MS, 'd']
      : absoluteDifference >= HOUR_IN_MS
        ? [HOUR_IN_MS, 'h']
        : [MINUTE_IN_MS, 'm'];
  const value = Math.max(1, Math.floor(absoluteDifference / divider));

  return `${differenceInMs < 0 ? '- ' : ''}${value}${unit}${showLeftLabel ? ' left' : ''}`;
};

const getSlaIndicatorSx = (isExpired: boolean, variant: 'badge' | 'plain') => ({
  alignItems: 'center',
  backgroundColor: variant === 'badge' ? (isExpired ? 'background.error' : 'background.success') : 'transparent',
  borderRadius: variant === 'badge' ? 1 : 0,
  color: isExpired ? 'error.main' : 'success.main',
  flexShrink: 0,
  gap: variant === 'badge' ? 0.5 : 0.75,
  px: variant === 'badge' ? 1 : 0,
  py: variant === 'badge' ? 0.5 : 0,
  whiteSpace: 'nowrap',
});

const SlaIndicator = ({ endDate, variant = 'badge' }: { endDate: string; variant?: 'badge' | 'plain' }) => {
  const { getCurrentDateTime, getMomentDateObj, getTimeDifference } = useDateService();
  const [currentDateTime, setCurrentDateTime] = useState(getCurrentDateTime);

  useEffect(() => {
    const intervalId = window.setInterval(() => setCurrentDateTime(getCurrentDateTime()), MINUTE_IN_MS);

    return () => window.clearInterval(intervalId);
  }, [getCurrentDateTime]);

  const slaEndDate = getMomentDateObj(endDate);

  if (!slaEndDate.isValid()) return null;

  const differenceInMs = getTimeDifference(currentDateTime, slaEndDate);
  const isExpired = differenceInMs < 0;
  const displayValue = getSlaDisplayValue(differenceInMs, variant === 'badge');

  return (
    <Stack direction="row" role="img" sx={getSlaIndicatorSx(isExpired, variant)} aria-label={`SLA: ${displayValue}`}>
      <HourglassEmptyOutlinedIcon sx={{ fontSize: 12 }} />
      <Typography component="span" variant="caption" color="inherit">
        {displayValue}
      </Typography>
    </Stack>
  );
};

export default SlaIndicator;
